// Google Tag Manager (GTM) Analytics Configuration
// Uses dataLayer for all event tracking
// Implements Consent Mode v2 with cookie consent integration

// Declare global window types for TypeScript
declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
    GA_INITIALIZED?: boolean;
  }
}

// Initialize dataLayer and set consent defaults
export const initGA = () => {
  if (typeof window === 'undefined') return;
  if (window.GA_INITIALIZED) return;

  // Initialize dataLayer (GTM will already have created it, but ensure it exists)
  window.dataLayer = window.dataLayer || [];

  // Set Consent Mode defaults based on stored consent
  const consentGiven = ((): boolean => {
    try {
      return localStorage.getItem('cookieConsent') === 'true';
    } catch {
      return false;
    }
  })();

  // Push consent default state to dataLayer
  window.dataLayer.push({
    event: 'consent_default',
    ad_storage: consentGiven ? 'granted' : 'denied',
    analytics_storage: consentGiven ? 'granted' : 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
  });

  window.GA_INITIALIZED = true;
};

// Helper function to push events to dataLayer
const pushToDataLayer = (eventData: any) => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    ...eventData,
    timestamp: new Date().toISOString(),
  });
};

// Track page views
export const pageview = (path: string) => {
  pushToDataLayer({
    event: 'page_view',
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
};

// Track generic events
export const event = ({
  action,
  category,
  label,
  value,
}: {
  action: string;
  category: string;
  label?: string;
  value?: number;
}) => {
  pushToDataLayer({
    event: action,
    event_category: category,
    event_label: label,
    event_value: value,
  });
};

// Enhanced event tracking for business conversions
export const trackConversion = (conversionType: string, value?: number, currency = 'USD') => {
  pushToDataLayer({
    event: 'conversion',
    conversion_type: conversionType,
    conversion_value: value,
    currency: currency,
  });
};

// Track form interactions
export const trackFormEvent = (formName: string, action: 'start' | 'complete' | 'abandon', step?: string) => {
  pushToDataLayer({
    event: `form_${action}`,
    form_name: formName,
    form_step: step,
    event_category: 'form_interaction',
  });
};

// Track button clicks and CTA interactions
export const trackCTAClick = (ctaName: string, location: string, destination?: string) => {
  pushToDataLayer({
    event: 'cta_click',
    cta_text: ctaName,
    cta_location: location,
    cta_destination: destination,
    event_category: 'engagement',
  });
};

// Track content engagement
export const trackContentEngagement = (contentType: string, action: string, contentId?: string) => {
  pushToDataLayer({
    event: `content_${action}`,
    content_type: contentType,
    content_id: contentId,
    event_category: 'content_engagement',
  });
};

// Track service interest
export const trackServiceInterest = (serviceName: string, action: 'view' | 'inquiry' | 'click') => {
  pushToDataLayer({
    event: `service_${action}`,
    service_name: serviceName,
    service_action: action,
    event_category: 'service_interest',
  });
};

// Track newsletter and lead generation
export const trackLeadGeneration = (source: string, leadType: 'newsletter' | 'contact' | 'demo' | 'consultation') => {
  pushToDataLayer({
    event: 'lead_generation',
    lead_type: leadType,
    lead_source: source,
    event_category: 'conversion',
  });
  
  // Also track as conversion
  trackConversion(`${leadType}_signup`);
};

// Update consent (e.g., when user accepts cookies)
export const updateConsent = (granted: boolean) => {
  pushToDataLayer({
    event: 'consent_update',
    ad_storage: granted ? 'granted' : 'denied',
    analytics_storage: granted ? 'granted' : 'denied',
  });
};
